import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Lock, ShoppingBag, Truck } from "lucide-react";
import { ProductSpotlight } from "@/components/shop/ProductSpotlight";
import { ProductDetails } from "@/components/shop/ProductDetails";
import { ProductCard } from "@/components/shop/ProductCard";
import { NotifyForm } from "@/components/shop/NotifyForm";
import { ReviewsSection } from "@/components/reviews/ReviewsSection";
import { Stars, formatRating } from "@/components/reviews/Stars";
import { QtyStepper } from "@/components/cart/CartLine";
import { MaskedLines } from "@/components/motion/MaskedLines";
import { useCart } from "@/context/CartContext";
import { useProduct, useProducts } from "@/hooks/useProducts";
import { scrollToId } from "@/hooks/useLenis";
import { Seo } from "@/components/seo/Seo";
import { SEO, productSeo } from "@/data/seo";
import { formatEUR } from "@/lib/format";
import { EASE } from "@/lib/motion";

const fade = (delay) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { delay, duration: 0.8, ease: EASE } });

const Related = ({ current, category }) => {
  const { data: all = [] } = useProducts();
  const related = [...all.filter((p) => p.slug !== current && p.category === category), ...all.filter((p) => p.category !== category)].slice(0, 4);
  if (!related.length) return null;
  return (
    <section className="mt-28 border-t border-copper/20 pt-20" data-testid="related-products">
      <p className="eyebrow">Completa il rituale</p>
      <h2 className="mt-4 font-display text-4xl font-medium text-navy sm:text-5xl">Ti potrebbe servire anche</h2>
      <div className="mt-12 grid grid-cols-1 gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-4">
        {related.map((p, i) => (
          <ProductCard key={p.slug} product={p} index={i} />
        ))}
      </div>
    </section>
  );
};

const RatingLink = ({ product }) =>
  product.rating_count > 0 ? (
    <motion.button {...fade(0.4)} type="button" onClick={() => scrollToId("recensioni")} data-testid="product-rating-link" className="mt-5 inline-flex items-center gap-3 text-[15px] text-navy underline-offset-4 hover:underline">
      <Stars value={product.rating_average} />
      <span>{formatRating(product.rating_average)} · {product.rating_count} {product.rating_count === 1 ? "recensione" : "recensioni"}</span>
    </motion.button>
  ) : null;

const NotFound = ({ offline, onRetry }) => (
  <div className="container-pf py-48 text-center" data-testid={offline ? "product-load-error" : "product-not-found"}>
    <Seo {...SEO.private} title="Prodotto non trovato | Perfero" />
    <h1 className="font-display text-5xl text-navy">{offline ? "Un attimo di pazienza" : "Prodotto non trovato"}</h1>
    {offline && <p className="mx-auto mt-5 max-w-md text-lg text-slate2">Non riusciamo a caricare il prodotto in questo momento. Riproviamo da soli tra pochi secondi.</p>}
    <div className="mt-10 flex flex-wrap justify-center gap-3">
      {offline && <button type="button" onClick={onRetry} data-testid="product-retry" className="btn-primary">Riprova ora</button>}
      <Link to="/collezione" className={offline ? "btn-ghost" : "btn-primary"}>Torna alla collezione</Link>
    </div>
  </div>
);

export default function ProductPage() {
  const { slug } = useParams();
  const { data: product, isLoading, isError, error, refetch } = useProduct(slug);
  const { add } = useCart();
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setQty(1);
  }, [slug]);

  if (isLoading) return <div className="min-h-screen bg-ivory" data-testid="product-loading" />;
  if (isError && error?.response?.status !== 404) return <NotFound offline onRetry={() => refetch()} />;
  if (isError || !product) return <NotFound />;
  const soldOut = product.stock <= 0;

  return (
    <div className="bg-ivory pb-28 pt-36 lg:pt-40" data-testid="product-page">
      <Seo {...productSeo(product)} />
      <div className="container-pf">
        <Link to="/collezione" className="inline-flex h-11 items-center gap-2 text-[15px] text-slate2 transition-colors hover:text-navy" data-testid="product-back-link">
          <ArrowLeft className="h-4 w-4" /> Torna alla collezione
        </Link>
        <div className="mt-8 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6">
            <div className="lg:sticky lg:top-36">
              <ProductSpotlight src={product.image} alt={`Perfero ${product.name}`} />
            </div>
          </div>
          <div className="lg:col-span-6 lg:pt-6">
            <motion.p {...fade(0.1)} className="eyebrow">{product.category} · {product.size}</motion.p>
            <h1 className="mt-5 font-display text-5xl font-medium leading-[1] text-navy sm:text-6xl" data-testid="product-title">
              <MaskedLines onLoad delay={0.15} lines={[product.name]} />
            </h1>
            <motion.p {...fade(0.35)} className="mt-6 font-display text-2xl italic text-copper-deep">{product.tagline}</motion.p>
            <RatingLink product={product} />
            <motion.div {...fade(0.45)} className="mt-10 flex items-end justify-between gap-6 border-y border-copper/20 py-8">
              <div>
                <p className="font-display text-5xl text-navy" data-testid="product-detail-price">{formatEUR(product.price)}</p>
                <p className="mt-1 text-[14px] text-slate2">IVA inclusa</p>
              </div>
              <p className={`text-[15px] font-medium ${soldOut ? "text-destructive" : "text-[#166534]"}`} data-testid="product-stock">
                {soldOut ? "Non disponibile" : product.stock < 10 ? `Ultimi ${product.stock} pezzi` : "Disponibile"}
              </p>
            </motion.div>
            <motion.div {...fade(0.55)} className="mt-8 flex flex-col gap-4 sm:flex-row">
              <QtyStepper value={qty} onChange={(q) => setQty(Math.max(1, Math.min(20, q)))} testid="product-qty" size="lg" />
              <button type="button" disabled={soldOut} onClick={() => add(product, qty)} data-testid="add-to-cart-button" className="btn-primary h-14 flex-1 text-lg">
                <ShoppingBag className="h-5 w-5" /> {soldOut ? "Non disponibile" : "Aggiungi al carrello"}
              </button>
            </motion.div>
            {soldOut && <NotifyForm product={product} />}
            <motion.ul {...fade(0.65)} className="mt-8 grid gap-3 text-[15px] text-slate2 sm:grid-cols-2">
              <li className="flex items-center gap-3"><Truck className="h-5 w-5 text-copper" strokeWidth={1.5} /> Spedizione in tutta Italia</li>
              <li className="flex items-center gap-3"><Lock className="h-5 w-5 text-copper" strokeWidth={1.5} /> Pagamento sicuro con Stripe</li>
            </motion.ul>
            <ProductDetails product={product} />
          </div>
        </div>
        <ReviewsSection slug={product.slug} />
        <Related current={product.slug} category={product.category} />
      </div>
    </div>
  );
}
